import React, { useRef, useImperativeHandle, forwardRef, useState } from "react";
import styles from "./Input.module.scss";
import SearchableSelect from "./SearchableSelect";
import Select from "./Select";
import FileDropzone from "./FileDropzone/FileDropzone";

/**
 * Customizable Input component with variant support
 *
 * @param {Object} props - Component props
 * @param {string} props.variant - Input style variant (light, dark, primary, secondary)
 * @param {string} props.label - Label text
 * @param {string} props.type - Input type
 * @param {string} props.placeholder - Placeholder text
 * @param {boolean} props.required - Whether the input is required
 * @param {string} props.name - Input name
 * @param {Array} props.options - Options for select inputs
 * @param {function} props.onChange - Change handler
 * @param {string} props.value - Controlled input value
 * @param {string} props.defaultValue - Uncontrolled input default value
 * @param {boolean} props.searchable - Whether select is searchable
 * @param {boolean} props.serializeInTopLevel - Serialization flag
 * @param {boolean} props.floating - Whether to use floating label
 * @param {string} props.accept - Accepted file types for file inputs
 * @param {number} props.maxFiles - Maximum number of files allowed for file inputs
 * @param {number} props.minFiles - Minimum number of files required for file inputs
 * @param {string} props.uploadUrl - URL to upload files to for file inputs
 */
const Input = forwardRef((props, ref) => {
	const {
		label,
		type,
		placeholder,
		required,
		name,
		options = [],
		onChange,
		value,
		defaultValue,
		searchable = true,
		serializeInTopLevel,
		variant = 'light',
		className = '',
		floating = true, // New prop with default value true for backward compatibility
		error,
		accept,
		uploadUrl,
		description,
		...rest
	} = props;

	const searchableSelectRef = useRef(null);
	const selectRef = useRef(null);
	const fileDropzoneRef = useRef(null);
	const [isFocused, setIsFocused] = useState(false);
	const isControlled = onChange && value !== undefined;
	const hasValue = isControlled ? !!value : !!defaultValue;

	useImperativeHandle(ref, () => ({
		reset: () => {
			if (searchableSelectRef.current) searchableSelectRef.current.reset();
			if (selectRef.current) selectRef.current.reset();
			if (fileDropzoneRef.current) fileDropzoneRef.current.reset();
		}
	}));

	const handleChange = (e) => {
		if (e.target && e.target.classList) e.target.classList.remove('is-invalid');
		if (onChange) onChange(e);
	};

	const handleFocus = () => setIsFocused(true);
	const handleBlur = () => setIsFocused(false);

	if (type === "hidden") {
		return (
			<input
				type="hidden"
				name={name}
				data-field={name}
				value={value || ""}
				serializeInTopLevel={serializeInTopLevel ? "" : undefined}
				{...rest}
			/>
		);
	}

	if (type === "checkbox" || type === "radio") {
		return (
			<div className={`${styles.Input} ${styles[`Input--${variant}`]} ${className}`}>
				<div className="form-check">
					<input
						type={type}
						className="form-check-input"
						required={required}
						data-field={name}
						name={name}
						value={value || ""}
						defaultChecked={defaultValue}
						serializeInTopLevel={serializeInTopLevel ? "" : undefined}
						onChange={handleChange}
						{...rest}
					/>
					<label 
						htmlFor={rest.id} 
						className="form-check-label">
						{label} {required && <span className="text-danger small ms-1">*</span>}
					</label>
				</div>
			</div>
		);
	}



	if (type === "file") {
			const {max: maxFiles,  min: minFiles, maxFileSize, acceptedMIMEtypes: accept, ...fileRestTemp} = rest; // rename an extract properties for file input
			const fileRest = {maxFiles, minFiles, maxFileSize, accept, ...fileRestTemp}; // spread the rest of the properties
		return (
			<div className={`${styles.Input} ${styles[`Input--${variant}`]} ${className}`}>
				<FileDropzone
					ref={fileDropzoneRef}
					title={label || "Dateien hochladen"}
					description={description || "Ziehe Dateien hierher oder klicke, um Dateien auszuwählen"}
					accept={accept}
					required={required}
					name={name}
					uploadUrl={uploadUrl}
					variant={variant}
					onChange={handleChange}
					error={error}
					isValid={!error}
					{...fileRest}
				/>
			</div>
		);
	}

	if (type === "select") {
		if (searchable && options.length > 10) {
			return (
				<SearchableSelect
					ref={searchableSelectRef}
					label={label}
					options={options}
					required={required}
					name={name}
					placeholder={placeholder}
					value={value}
					serializeInTopLevel={serializeInTopLevel ? "" : undefined}
					onChange={handleChange}
					className={`${styles.Input} ${styles[`Input--${variant}`]} ${className}`}
					floating={floating}
					error={error}
					{...rest}
				/>
			);
		} else {
			return (
				<Select
					ref={selectRef}
					label={label}
					options={options}
					required={required}
					name={name}
					placeholder={placeholder}
					value={value}
					serializeInTopLevel={serializeInTopLevel ? "" : undefined}
					onChange={handleChange}
					className={`${styles.Input} ${styles[`Input--${variant}`]} ${className}`}
					floating={floating}
					error={error}
					{...rest}
				/>
			);
		}
	}

	// Standard (non-floating) input with label
	if (!floating) {
		return (
			<div className={`${styles.Input} ${styles[`Input--${variant}`]} ${className} mb-3 ${!label ? 'position-relative' : ''}`}>
				{label && (
					<label 
						htmlFor={rest.id || name} 
						className="form-label">
						{label}
						{required && <span className="text-danger ms-1">*</span>}
						{placeholder && <span className="text-muted small ms-2">({placeholder})</span>}
					</label>
				)}
				<div className={`${!label && required ? 'position-relative' : ''}`}>
					{type === "textarea" ? (
						<textarea
							className={`form-control ${error ? 'is-invalid' : ''}`}
							placeholder={placeholder || ""}
							required={required}
							name={name}
							rows="3"
							data-field={name}
							onChange={handleChange}
							onFocus={handleFocus}
							onBlur={handleBlur}
							{...(isControlled ? { value } : { defaultValue: defaultValue || "" })}
							{...rest}
							/>
					) : (
						<input
							type={type}
							className={`form-control ${error ? 'is-invalid' : ''}`}
							placeholder={placeholder || ""}
							required={required}
							name={name}
							data-field={name}
							onChange={handleChange}
							onFocus={handleFocus}
							onBlur={handleBlur}
							{...(isControlled ? { value } : { defaultValue: defaultValue || "" })}
							{...rest}
						/>
					)}
					{required && !label && <span className="required-indicator">*</span>}
				</div>
				{error && <div className="invalid-feedback">{error}</div>}
			</div>
		);
	}

	// Floating label input (original implementation)
	return (
		<div className={`${styles.Input} ${styles[`Input--${variant}`]} position-relative ${className}`}>
			<div className="form-floating placeholder-shown">
				{type === "textarea" ? (
					<>
						<textarea
							className={`form-control custom-control ${error ? 'is-invalid' : ''}`}
							placeholder={placeholder || " "}
							required={required}
							name={name}
							rows="3"
							data-field={name}
							serializeInTopLevel={serializeInTopLevel ? "" : undefined}
							onChange={handleChange}
							onFocus={handleFocus}
							onBlur={handleBlur}
							{...(isControlled ? { value } : { defaultValue: defaultValue || "" })}
							{...rest}
						/>
						<label 
							htmlFor={rest.id || name} 
							className={isFocused || hasValue ? 'focused' : ''}>
							{label}
							{placeholder && <span className="text-info small ms-2">({placeholder})</span>}
						</label>
					</>
				) : (
					<>
						<input
							type={type}
							className={`form-control custom-control ${error ? 'is-invalid' : ''}`}
							placeholder={placeholder || " "}
							required={required}
							name={name}
							data-field={name}
							serializeInTopLevel={serializeInTopLevel ? "" : undefined}
							onChange={handleChange}
							onFocus={handleFocus}
							onBlur={handleBlur}
							{...(isControlled ? { value } : { defaultValue: defaultValue || "" })}
							{...rest}
						/>
						<label 
							htmlFor={rest.id || name}
							className={isFocused || hasValue ? 'focused' : ''}>
							{label}
							{placeholder && <span className="text-info small ms-2">({placeholder})</span>}
						</label>
					</>
				)}
			</div>
			{required && <span className="required-indicator">*</span>}
			{error && <div className="invalid-feedback d-block">{error}</div>}
		</div>
	);
});

Input.displayName = 'Input';

export default Input;
